/**
 * Transaction Card Component
 * 
 * Displays a financial transaction (revenue or expense) with actions.
 */
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CurrencyDisplay } from './CurrencyDisplay';
import { ProgressIndicator } from './ProgressIndicator';
import { formatDateShortPtBR } from '@/lib/date';
import {
  MoreVertical,
  Check,
  Edit,
  Trash2,
  Copy,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Info,
  LucideIcon,
} from 'lucide-react';
import type { RealizationStatus } from '@/types/financial';

interface TransactionCardProps {
  id: string;
  name: string;
  type: 'revenue' | 'expense';
  subtype?: string;
  plannedAmount: number;
  realizedAmount: number;
  status: RealizationStatus;
  hideVariance?: boolean;
  /** Marca a linha como o "Saldo do PO anterior" auto-gerenciado: exibe nota explicativa. */
  isBalanceCarryover?: boolean;
  category?: string;
  dueDate?: string;
  realizedDate?: string;
  isOverdue?: boolean;
  icon?: LucideIcon;
  onRealize?: () => void;
  realizeLabel?: string;
  realizeTourId?: string;
  showRealizeInDropdown?: boolean;
  /** Permite manter o botão "Receber/Pagar" visível mesmo com status=realized (ex.: receitas fracionadas podem ter lançamentos extras). */
  allowRealizeWhenRealized?: boolean;
  disableRealize?: boolean;
  onEdit?: () => void;
  onEditRealized?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  onUndo?: () => void;
  className?: string;
}

const statusConfig: Record<RealizationStatus, { label: string; icon: LucideIcon; color: string }> = {
  pending: {
    label: 'Pendente',
    icon: Clock,
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  },
  realized: {
    label: 'Realizado',
    icon: CheckCircle2,
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  partially_realized: {
    label: 'Parcial',
    icon: RefreshCw,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  },
  cancelled: {
    label: 'Cancelado',
    icon: XCircle,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  },
};

export function TransactionCard({
  id,
  name,
  type,
  subtype,
  plannedAmount,
  realizedAmount,
  status,
  hideVariance = false,
  isBalanceCarryover = false,
  category,
  dueDate,
  realizedDate,
  isOverdue = false,
  icon: Icon,
  onRealize,
  realizeLabel,
  realizeTourId,
  showRealizeInDropdown = true,
  allowRealizeWhenRealized = false,
  disableRealize = false,
  onEdit,
  onEditRealized,
  onDelete,
  onCopy,
  onUndo,
  className,
}: Readonly<TransactionCardProps>) {
  const statusInfo = statusConfig[status] || statusConfig.pending;
  const StatusIcon = statusInfo.icon;
  const variance = plannedAmount - realizedAmount;
  const canRealize =
    !!onRealize &&
    (status === 'pending' ||
      status === 'partially_realized' ||
      (allowRealizeWhenRealized && status === 'realized'));
  const isRealizeDisabled = disableRealize;
  const realizeActionLabel = realizeLabel || (type === 'revenue' ? 'Receber' : 'Pagar');

  const typeColor = type === 'revenue' ? 'text-emerald-600' : 'text-rose-600';
  // Ordem de precedência preservada: realizado ganha de vencido, que ganha de
  // parcialmente realizado.
  const obterCorDaBorda = () => {
    if (status === 'realized') return 'border-l-emerald-500';
    if (isOverdue) return 'border-l-rose-500';
    if (status === 'partially_realized') return 'border-l-blue-500';
    return 'border-l-amber-500';
  };
  const borderColor = obterCorDaBorda();

  return (
    <Card className={cn('border-l-4 overflow-hidden transition-shadow hover:shadow-md', borderColor, className)}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
          {/* Top/Left section - Icon and info */}
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            {Icon && (
              <div className={cn('p-1.5 sm:p-2 rounded-lg bg-muted/50 shrink-0', typeColor)}>
                <Icon className="h-4 w-4" />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="font-medium truncate text-sm sm:text-base">{name}</h4>
                {subtype && (
                  <Badge variant="outline" className="text-[10px] sm:text-xs shrink-0">
                    {subtype}
                  </Badge>
                )}
                {/* Actions dropdown - mobile: top-right corner */}
                <div className="sm:hidden ml-auto shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canRealize && showRealizeInDropdown && (
                        <DropdownMenuItem
                          onClick={isRealizeDisabled ? undefined : onRealize}
                          disabled={isRealizeDisabled}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          {realizeActionLabel}
                        </DropdownMenuItem>
                      )}
                      {status === 'realized' && onUndo && (
                        <DropdownMenuItem onClick={onUndo}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Desfazer realização
                        </DropdownMenuItem>
                      )}
                      {onEdit && (
                        <DropdownMenuItem onClick={onEdit}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar Planejado
                        </DropdownMenuItem>
                      )}
                      {onEditRealized && (
                        <DropdownMenuItem onClick={onEditRealized}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar Realizado
                        </DropdownMenuItem>
                      )}
                      {onCopy && (
                        <DropdownMenuItem onClick={onCopy}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={onDelete} className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <Badge className={cn('text-[10px] sm:text-xs', statusInfo.color)}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </Badge>
                {category && (
                  <Badge variant="secondary" className="text-[10px] sm:text-xs">
                    {category}
                  </Badge>
                )}
                {isOverdue && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge variant="destructive" className="text-[10px] sm:text-xs">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Vencido
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Esta transação está vencida!</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Progress bar */}
              <div className="pt-1">
                <ProgressIndicator
                  value={realizedAmount}
                  max={plannedAmount}
                  showPercentage={false}
                  size="sm"
                  colorScheme={
                    status === 'realized'
                      ? 'success'
                      : type === 'expense' && realizedAmount > plannedAmount
                      ? 'danger'
                      : 'default'
                  }
                />
              </div>

              {/* Nota do lançamento auto-gerenciado "Saldo do PO anterior" */}
              {isBalanceCarryover && (
                <div className="flex items-start gap-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5 mt-1">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                  <p className="text-[10px] sm:text-xs leading-snug text-amber-800 dark:text-amber-300">
                    Saldo calculado automaticamente a partir do PO anterior. Você pode editá-lo
                    ou excluí-lo, mas após isso, quaisquer alterações no período anterior
                    substituirá sua edição.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom/Right section - Amounts and actions */}
          <div className="flex items-center sm:items-start justify-between sm:justify-end gap-2 sm:gap-3 pl-7 sm:pl-0">
            <div className="text-left sm:text-right space-y-0.5">
              <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Planejado:</span>
                <CurrencyDisplay value={plannedAmount} size="sm" />
                {dueDate && formatDateShortPtBR(dueDate) && (
                  <span className="text-[10px] sm:text-xs text-muted-foreground">({formatDateShortPtBR(dueDate)})</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                <span className="text-[10px] sm:text-xs text-muted-foreground">Realizado:</span>
                <CurrencyDisplay
                  value={realizedAmount}
                  size="sm"
                  colorize
                />
                {realizedDate && formatDateShortPtBR(realizedDate) && status !== 'pending' && (
                  <span className="text-[10px] sm:text-xs text-muted-foreground">({formatDateShortPtBR(realizedDate)})</span>
                )}
              </div>
              {!hideVariance && variance !== 0 && status !== 'pending' && (
                <div className="flex items-center gap-1.5 sm:gap-2 sm:justify-end">
                  <span className="text-[10px] sm:text-xs text-muted-foreground">Variação:</span>
                  <CurrencyDisplay
                    value={type === 'revenue' ? -variance : variance}
                    size="xs"
                    showSign
                    colorize
                  />
                </div>
              )}
            </div>

            {/* Botão de realizar destacado para pendentes e parciais */}
            {canRealize && (
              <Button
                variant="default"
                size="sm"
                onClick={onRealize}
                disabled={isRealizeDisabled}
                data-tour-id={realizeTourId}
                className={cn(
                  "h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm font-medium shrink-0",
                  isRealizeDisabled
                    ? "bg-muted text-muted-foreground"
                    : type === 'revenue' 
                      ? "bg-emerald-600 hover:bg-emerald-700" 
                      : "bg-rose-600 hover:bg-rose-700"
                )}
              >
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                {realizeActionLabel}
              </Button>
            )}

            {/* Actions dropdown - desktop only */}
            <div className="hidden sm:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canRealize && showRealizeInDropdown && (
                    <DropdownMenuItem
                      onClick={isRealizeDisabled ? undefined : onRealize}
                      disabled={isRealizeDisabled}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      {realizeActionLabel}
                    </DropdownMenuItem>
                  )}
                  {status === 'realized' && onUndo && (
                    <DropdownMenuItem onClick={onUndo}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Desfazer realização
                    </DropdownMenuItem>
                  )}
                  {onEdit && (
                    <DropdownMenuItem onClick={onEdit}>
                      <Edit className="h-4 w-4 mr-2" />
                      Editar Planejado
                    </DropdownMenuItem>
                  )}
                  {onEditRealized && (
                    <DropdownMenuItem onClick={onEditRealized}>
                      <Edit className="h-4 w-4 mr-2" />
                      Editar Realizado
                    </DropdownMenuItem>
                  )}
                  {onCopy && (
                    <DropdownMenuItem onClick={onCopy}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicar
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={onDelete} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default TransactionCard;
